import {
  alertTriangleIcon,
  checkIcon,
  historyIcon,
  LibraryIcon,
  pencilIcon,
  PlusIcon,
  RetryIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import clsx from "clsx";
import React, {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import type { BinaryFiles } from "@excalidraw/excalidraw/types";

import { atom, useAtom } from "../app-jotai";

import { LibraryAPI } from "./api";
import {
  generateThumbnail,
  restoreDrawingElements,
  serializeDrawing,
} from "./drawings";
import {
  flushSave,
  getCurrentDrawingId,
  getLibraryPath,
  openDrawing,
  subscribeCurrentDrawingId,
} from "./editor";
import { formatDateTime, lt } from "./i18n";
import {
  loadConflict,
  loadServerVersion,
  resolveConflict,
  restoreServerVersion,
} from "./remote";
import {
  defaultDeviceName,
  getConflictMode,
  getDeviceName,
  setConflictMode,
  setDeviceName,
} from "./settings";
import { logOut, useProxySession } from "./session";
import { DrawingStore, onLibraryChange } from "./store";
import { SyncEngine } from "./sync";

import "./library.scss";

import type { FolderTree } from "./folders";
import type { ConflictMode } from "./settings";
import type { SyncStatus } from "./sync";
import type {
  ConflictResolution,
  DrawingId,
  DrawingMeta,
  DrawingScene,
  FolderId,
  ServerVersionInfo,
} from "./types";

export { formatDateTime };

export const useSyncStatus = () =>
  useSyncExternalStore(SyncEngine.subscribe, SyncEngine.getStatus);

export const sanitizeFileName = (name: string) =>
  name
    // eslint-disable-next-line no-control-regex
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "_")
    .trim()
    .slice(0, 120) || "drawing";

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const cloudIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M7 18a4.6 4.4 0 0 1 0-9 5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-12" />
  </svg>
);

const cloudOffIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9.58 5.548c.24-.11.492-.207.752-.286a5.05 4.5 0 0 1 7.668 3.738h1a3.5 3.5 0 0 1 2.5 5.95m-1.5 .55h-13a4.6 4.4 0 0 1-.5-8.78" />
    <path d="M3 3l18 18" />
  </svg>
);

export const logOutIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 8v-2a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-2" />
    <path d="M9 12h12l-3-3" />
    <path d="M18 15l3-3" />
  </svg>
);

export const settingsGearIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37 1 .608 2.296.07 2.572-1.065z" />
    <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0-6 0" />
  </svg>
);

const describeStatus = (status: SyncStatus) => {
  if (status.conflicts && status.state !== "syncing") {
    return {
      icon: alertTriangleIcon,
      label: lt("conflictsCount", { count: status.conflicts }),
      state: "conflict",
    };
  }
  switch (status.state) {
    case "syncing":
      return { icon: RetryIcon, label: lt("syncSyncing"), state: "syncing" };
    case "offline":
      return { icon: cloudOffIcon, label: lt("syncOffline"), state: "offline" };
    case "error":
      return {
        icon: alertTriangleIcon,
        label: lt("syncError"),
        state: "error",
      };
    case "signedOut":
      return {
        icon: alertTriangleIcon,
        label: lt("syncSignedOut"),
        state: "conflict",
      };
    default:
      return status.pending
        ? {
            icon: cloudIcon,
            label: lt("syncPending", { count: status.pending }),
            state: "idle",
          }
        : { icon: checkIcon, label: lt("syncSynced"), state: "idle" };
  }
};

export const SyncStatusIndicator = ({
  compact = false,
  className,
  onClick,
}: {
  compact?: boolean;
  className?: string;
  /** defaults to "sync now" */
  onClick?: () => void;
}) => {
  const status = useSyncStatus();
  const { icon, label, state } = describeStatus(status);
  const title = [
    label,
    status.error,
    lt("lastSync", {
      time: status.lastSyncAt ? formatDateTime(status.lastSyncAt) : lt("never"),
    }),
    status.state === "signedOut"
      ? lt("syncSignIn")
      : onClick
      ? null
      : lt("syncNow"),
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <button
      type="button"
      className={clsx(
        "exdl-sync",
        `exdl-sync--${state}`,
        { "exdl-sync--compact": compact },
        className,
      )}
      title={title}
      aria-label={title}
      onClick={() =>
        status.state === "signedOut"
          ? SyncEngine.signIn()
          : onClick
          ? onClick()
          : SyncEngine.syncNow()
      }
    >
      <span className="exdl-icon">{icon}</span>
      {!compact && <span className="exdl-sync__label">{label}</span>}
    </button>
  );
};

export const Modal = ({
  title,
  children,
  onClose,
  actions,
  className,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  actions?: React.ReactNode;
  className?: string;
}) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  return (
    <div
      className="exdl-modal__backdrop"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={clsx("exdl-modal", className)}
        role="dialog"
        aria-modal="true"
        // keep editor shortcuts away from inputs and buttons
        onKeyDown={(event) => event.stopPropagation()}
      >
        <h2 className="exdl-modal__title">{title}</h2>
        <div className="exdl-modal__body">{children}</div>
        {actions && <div className="exdl-modal__actions">{actions}</div>}
      </div>
    </div>
  );
};

export const RenameDialog = ({
  initialName,
  onSubmit,
  onClose,
  title = lt("renameTitle"),
  label = lt("drawingName"),
}: {
  initialName: string;
  onSubmit: (name: string) => void;
  onClose: () => void;
  title?: string;
  label?: string;
}) => {
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.select();
  }, []);
  const submit = () => {
    const trimmed = name.trim();
    if (trimmed) {
      onSubmit(trimmed);
    }
  };
  return (
    <Modal
      title={title}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="exdl-button" onClick={onClose}>
            {lt("cancel")}
          </button>
          <button
            type="button"
            className="exdl-button exdl-button--primary"
            disabled={!name.trim()}
            onClick={submit}
          >
            {lt("save")}
          </button>
        </>
      }
    >
      <input
        ref={inputRef}
        className="exdl-input"
        aria-label={label}
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            submit();
          }
        }}
        autoFocus
      />
    </Modal>
  );
};

// -----------------------------------------------------------------------------
// conflicts
// -----------------------------------------------------------------------------

const useSceneThumbnail = (
  scene: DrawingScene | null | undefined,
  files: BinaryFiles | null | undefined,
) => {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (scene && files) {
      generateThumbnail(
        restoreDrawingElements(scene.elements),
        scene.appState,
        files,
      )
        .then((result) => !cancelled && setThumbnail(result))
        .catch((error) => console.warn(error));
    }
    return () => {
      cancelled = true;
    };
  }, [scene, files]);
  return thumbnail;
};

const VersionPreview = ({
  title,
  device,
  date,
  thumbnail,
}: {
  title: string;
  device: string | null;
  date: number;
  thumbnail: string | null;
}) => (
  <figure className="exdl-version-preview">
    <div className="exdl-version-preview__image">
      {thumbnail ? <img src={thumbnail} alt="" /> : <span>…</span>}
    </div>
    <figcaption>
      <strong>{title}</strong>
      <span>{device || lt("unknownDevice")}</span>
      <span>{formatDateTime(date)}</span>
    </figcaption>
  </figure>
);

const RESOLUTIONS: {
  resolution: ConflictResolution;
  label: Parameters<typeof lt>[0];
  hint: Parameters<typeof lt>[0];
}[] = [
  { resolution: "merge", label: "mergeBoth", hint: "mergeBothHint" },
  { resolution: "both", label: "keepBoth", hint: "keepBothHint" },
  { resolution: "mine", label: "keepMine", hint: "keepMineHint" },
  { resolution: "theirs", label: "useTheirs", hint: "useTheirsHint" },
];

export const ConflictDialog = ({
  id,
  onClose,
  onResolved,
}: {
  id: DrawingId;
  onClose: () => void;
  onResolved?: () => void;
}) => {
  const [conflict, setConflict] = useState<Awaited<
    ReturnType<typeof loadConflict>
  > | null>(null);
  const [busy, setBusy] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    let cancelled = false;
    // make sure the local side includes the latest edits
    flushSave()
      .then(() => loadConflict(id))
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result) {
          setConflict(result);
        } else {
          onCloseRef.current();
        }
      })
      .catch((error) => console.error(error));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const localThumbnail = useSceneThumbnail(
    conflict?.local.scene,
    conflict?.local.files,
  );
  const remoteThumbnail = useSceneThumbnail(
    conflict?.remote.scene,
    conflict?.remote.files,
  );

  const resolve = async (resolution: ConflictResolution) => {
    setBusy(true);
    try {
      await flushSave();
      await resolveConflict(id, resolution);
      SyncEngine.syncNow();
      onResolved?.();
      onClose();
    } catch (error) {
      console.error(error);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={lt("conflictTitle")}
      onClose={onClose}
      className="exdl-modal--wide"
    >
      {!conflict ? (
        <p>{lt("loading")}</p>
      ) : (
        <>
          <p>{lt("conflictDescription", { name: conflict.local.meta.name })}</p>
          <div className="exdl-conflict__versions">
            <VersionPreview
              title={lt("thisDevice")}
              device={getDeviceName()}
              date={conflict.local.meta.updatedAt}
              thumbnail={localThumbnail}
            />
            <VersionPreview
              title={lt("otherDevice")}
              device={conflict.local.meta.conflict!.remoteDevice}
              date={conflict.local.meta.conflict!.remoteUpdatedAt}
              thumbnail={remoteThumbnail}
            />
          </div>
          <div className="exdl-conflict__choices">
            {RESOLUTIONS.map(({ resolution, label, hint }) => (
              <button
                key={resolution}
                type="button"
                className={clsx("exdl-choice", {
                  "exdl-choice--primary": resolution === "merge",
                })}
                disabled={busy}
                onClick={() => resolve(resolution)}
              >
                <strong>{lt(label)}</strong>
                <span>{lt(hint)}</span>
              </button>
            ))}
          </div>
          <div className="exdl-modal__actions">
            <button
              type="button"
              className="exdl-button"
              disabled={busy}
              onClick={onClose}
            >
              {lt("decideLater")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

// -----------------------------------------------------------------------------
// version history
// -----------------------------------------------------------------------------

export const HistoryDialog = ({
  id,
  onClose,
  onRestored,
}: {
  id: DrawingId;
  onClose: () => void;
  onRestored?: () => void;
}) => {
  const [versions, setVersions] = useState<ServerVersionInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!navigator.onLine) {
      setError(lt("historyOffline"));
      return;
    }
    // push pending changes first, so the list shows them as current
    flushSave()
      .then(() => SyncEngine.syncNow())
      .then(() => LibraryAPI.listVersions(id))
      .then((list) => !cancelled && setVersions(list))
      .catch((error) => {
        console.warn(error);
        if (!cancelled) {
          setError(
            error?.status === 404 ? lt("historyEmpty") : lt("historyOffline"),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const restore = async (version: number) => {
    setBusy(true);
    try {
      await flushSave();
      await restoreServerVersion(id, version);
      SyncEngine.syncNow();
      onRestored?.();
      onClose();
    } catch (error: any) {
      console.error(error);
      setError(error?.message ?? String(error));
      setBusy(false);
    }
  };

  const download = async (entry: ServerVersionInfo) => {
    try {
      const { scene, files } = await loadServerVersion(id, entry.version);
      downloadBlob(
        new Blob([serializeDrawing({ scene, files })], {
          type: "application/json",
        }),
        `${sanitizeFileName(entry.name)} (${new Date(entry.savedAt)
          .toISOString()
          .slice(0, 16)
          .replace(/[:T]/g, "-")}).excalidraw`,
      );
    } catch (error: any) {
      setError(error?.message ?? String(error));
    }
  };

  return (
    <Modal
      title={lt("versionHistory")}
      onClose={onClose}
      className="exdl-modal--wide"
      actions={
        <button type="button" className="exdl-button" onClick={onClose}>
          {lt("close")}
        </button>
      }
    >
      {error && <p className="exdl-pre">{error}</p>}
      {!error && !versions && <p>{lt("loading")}</p>}
      {versions && (
        <>
          <p className="exdl-muted">{lt("historyHint")}</p>
          {versions.length < 2 && <p>{lt("historyEmpty")}</p>}
          <ul className="exdl-history">
            {versions.map((entry) => (
              <li key={entry.version} className="exdl-history__item">
                <div className="exdl-history__thumb">
                  {entry.thumbnail && <img src={entry.thumbnail} alt="" />}
                </div>
                <div className="exdl-history__info">
                  <strong>
                    {formatDateTime(entry.savedAt)}
                    {entry.isCurrent && (
                      <span className="exdl-badge">{lt("currentVersion")}</span>
                    )}
                  </strong>
                  <span>{entry.device || lt("unknownDevice")}</span>
                  <span className="exdl-muted">{entry.name}</span>
                </div>
                <div className="exdl-history__actions">
                  <button
                    type="button"
                    className="exdl-button"
                    disabled={busy}
                    onClick={() => download(entry)}
                  >
                    {lt("download")}
                  </button>
                  {!entry.isCurrent &&
                    (confirming === entry.version ? (
                      <button
                        type="button"
                        className="exdl-button exdl-button--primary"
                        disabled={busy}
                        title={lt("restoreConfirm", {
                          date: formatDateTime(entry.savedAt),
                        })}
                        onClick={() => restore(entry.version)}
                      >
                        {lt("confirm")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="exdl-button"
                        disabled={busy}
                        onClick={() => setConfirming(entry.version)}
                      >
                        {lt("restoreVersion")}
                      </button>
                    ))}
                </div>
                {confirming === entry.version && (
                  <p className="exdl-history__confirm">
                    {lt("restoreConfirm", {
                      date: formatDateTime(entry.savedAt),
                    })}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
};

// -----------------------------------------------------------------------------
// settings
// -----------------------------------------------------------------------------

export const SettingsDialog = ({ onClose }: { onClose: () => void }) => {
  const [deviceName, setDeviceNameValue] = useState(getDeviceName);
  const [mode, setMode] = useState<ConflictMode>(getConflictMode);
  const save = () => {
    const name = deviceName.trim() || defaultDeviceName();
    if (name !== getDeviceName()) {
      setDeviceName(name);
      SyncEngine.syncNow();
    }
    setConflictMode(mode);
    onClose();
  };
  return (
    <Modal
      title={lt("settings")}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="exdl-button" onClick={onClose}>
            {lt("cancel")}
          </button>
          <button
            type="button"
            className="exdl-button exdl-button--primary"
            onClick={save}
          >
            {lt("save")}
          </button>
        </>
      }
    >
      <label className="exdl-field">
        <span className="exdl-field__label">{lt("deviceName")}</span>
        <input
          className="exdl-input"
          value={deviceName}
          placeholder={defaultDeviceName()}
          onChange={(event) => setDeviceNameValue(event.target.value)}
        />
        <span className="exdl-muted">{lt("deviceNameHint")}</span>
      </label>
      <fieldset className="exdl-field">
        <legend className="exdl-field__label">{lt("conflictModeLabel")}</legend>
        {(["ask", "merge"] as const).map((value) => (
          <label key={value} className="exdl-checkbox">
            <input
              type="radio"
              name="conflict-mode"
              checked={mode === value}
              onChange={() => setMode(value)}
            />
            {lt(value === "ask" ? "conflictModeAsk" : "conflictModeMerge")}
          </label>
        ))}
      </fieldset>
    </Modal>
  );
};

// -----------------------------------------------------------------------------
// editor UI
// -----------------------------------------------------------------------------

type EditorDialog = "rename" | "history" | "conflict" | null;

const editorDialogAtom = atom<EditorDialog>(null);

/** library metadata of the drawing open in the editor */
const useCurrentDrawingMeta = () => {
  const id = useSyncExternalStore(
    subscribeCurrentDrawingId,
    getCurrentDrawingId,
  );
  const [meta, setMeta] = useState<DrawingMeta | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      if (!id) {
        setMeta(null);
        return;
      }
      DrawingStore.getMeta(id)
        .then((result) => !cancelled && setMeta(result ?? null))
        .catch((error) => console.warn(error));
    };
    load();
    const unsubscribe = onLibraryChange((event) => {
      if (id && event.ids.includes(id)) {
        load();
      }
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [id]);
  return meta?.id === id ? meta : null;
};

export const LibraryMainMenuItems = () => {
  const [, setDialog] = useAtom(editorDialogAtom);
  // the library opens on the drawing's folder, new drawings go there too
  const meta = useCurrentDrawingMeta();
  const folderId = meta?.folderId ?? null;
  const proxySession = useProxySession();
  return (
    <>
      <MainMenu.Item
        icon={LibraryIcon}
        onSelect={() => window.location.assign(getLibraryPath(folderId))}
      >
        {lt("drawingsLibrary")}
      </MainMenu.Item>
      <MainMenu.Item
        icon={PlusIcon}
        onSelect={() => openDrawing("new", folderId)}
      >
        {lt("newDrawing")}
      </MainMenu.Item>
      <MainMenu.Item icon={pencilIcon} onSelect={() => setDialog("rename")}>
        {lt("renameCurrent")}
      </MainMenu.Item>
      <MainMenu.Item icon={historyIcon} onSelect={() => setDialog("history")}>
        {lt("versionHistory")}
      </MainMenu.Item>
      {proxySession && (
        <MainMenu.Item icon={logOutIcon} onSelect={() => logOut(proxySession)}>
          {lt("logOut", { user: proxySession.user })}
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
    </>
  );
};

/** sync status in the editor: opens the conflict dialog when the open
 * drawing is in conflict */
export const EditorSyncIndicator = () => {
  const meta = useCurrentDrawingMeta();
  const [, setDialog] = useAtom(editorDialogAtom);
  return (
    <SyncStatusIndicator
      compact
      onClick={
        meta?.conflict
          ? () => setDialog("conflict")
          : () => SyncEngine.syncNow()
      }
    />
  );
};

export const EditorLibraryDialogs = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const [dialog, setDialog] = useAtom(editorDialogAtom);
  const meta = useCurrentDrawingMeta();
  const shownConflictRef = useRef<number | null>(null);
  const conflictDetectedAt = meta?.conflict?.detectedAt ?? null;

  // open the conflict dialog once per detected conflict
  useEffect(() => {
    if (
      conflictDetectedAt !== null &&
      shownConflictRef.current !== conflictDetectedAt
    ) {
      shownConflictRef.current = conflictDetectedAt;
      setDialog((current) => current ?? "conflict");
    }
  }, [conflictDetectedAt, setDialog]);

  const close = useRef(() => setDialog(null)).current;

  if (!excalidrawAPI || !dialog) {
    return null;
  }
  const id = getCurrentDrawingId();

  switch (dialog) {
    case "rename":
      return (
        <RenameDialog
          initialName={excalidrawAPI.getName()}
          onClose={close}
          onSubmit={(name) => {
            excalidrawAPI.updateScene({ appState: { name } });
            close();
          }}
        />
      );
    case "history":
      return id && meta ? (
        <HistoryDialog
          id={id}
          onClose={close}
          onRestored={() =>
            excalidrawAPI.setToast({ message: lt("versionRestored") })
          }
        />
      ) : (
        <Modal title={lt("versionHistory")} onClose={close}>
          <p>{lt("historyEmpty")}</p>
        </Modal>
      );
    case "conflict":
      return id && meta?.conflict ? (
        <ConflictDialog
          id={id}
          onClose={close}
          onResolved={() =>
            excalidrawAPI.setToast({ message: lt("conflictResolved") })
          }
        />
      ) : null;
  }
  return null;
};

// -----------------------------------------------------------------------------
// folders
// -----------------------------------------------------------------------------

export const folderIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M5 4h4l3 3h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2" />
  </svg>
);

/** picks the destination folder for "Move to…" */
export const MoveToDialog = ({
  tree,
  movingFolderIds,
  currentFolderId,
  onSubmit,
  onClose,
}: {
  tree: FolderTree;
  /** folders being moved: they and their sub-folders aren't valid targets */
  movingFolderIds: readonly FolderId[];
  currentFolderId: FolderId | null;
  onSubmit: (folderId: FolderId | null) => void;
  onClose: () => void;
}) => {
  const [target, setTarget] = useState<FolderId | null>(currentFolderId);
  const excluded = new Set(
    movingFolderIds.flatMap((id) => [...tree.subtreeOf(id)]),
  );

  const renderLevel = (parentId: FolderId | null, depth: number) =>
    tree.childrenOf(parentId).map((folder) => (
      <React.Fragment key={folder.id}>
        <li>
          <button
            type="button"
            className={clsx("exdl-picker__item", {
              "exdl-picker__item--selected": target === folder.id,
            })}
            style={{ paddingInlineStart: `${0.75 + depth * 1.25}rem` }}
            disabled={excluded.has(folder.id)}
            onClick={() => setTarget(folder.id)}
            onDoubleClick={() => onSubmit(folder.id)}
          >
            <span className="exdl-icon">{folderIcon}</span>
            {folder.name}
          </button>
        </li>
        {!excluded.has(folder.id) && renderLevel(folder.id, depth + 1)}
      </React.Fragment>
    ));

  return (
    <Modal
      title={lt("moveTo")}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="exdl-button" onClick={onClose}>
            {lt("cancel")}
          </button>
          <button
            type="button"
            className="exdl-button exdl-button--primary"
            onClick={() => onSubmit(target)}
          >
            {lt("move")}
          </button>
        </>
      }
    >
      <ul className="exdl-picker">
        <li>
          <button
            type="button"
            className={clsx("exdl-picker__item", {
              "exdl-picker__item--selected": target === null,
            })}
            onClick={() => setTarget(null)}
            onDoubleClick={() => onSubmit(null)}
          >
            <span className="exdl-icon">{LibraryIcon}</span>
            {lt("rootFolder")}
          </button>
        </li>
        {renderLevel(null, 1)}
      </ul>
    </Modal>
  );
};

/** confirms the deletion of folders, asking what to do with their contents */
export const DeleteFoldersDialog = ({
  folderNames,
  drawingCount,
  folderCount,
  parentName,
  onSubmit,
  onClose,
}: {
  folderNames: string[];
  /** drawings inside (sub-folders included) */
  drawingCount: number;
  /** sub-folders inside */
  folderCount: number;
  /** where contents go with "move" (the root for top-level folders) */
  parentName: string;
  onSubmit: (mode: "trash" | "moveUp") => void;
  onClose: () => void;
}) => {
  const isEmpty = !drawingCount && !folderCount;
  const names = folderNames.map((name) => `“${name}”`).join(", ");
  return (
    <Modal
      title={lt("deleteFolderTitle", { count: folderNames.length })}
      onClose={onClose}
      actions={
        <button type="button" className="exdl-button" onClick={onClose}>
          {lt("cancel")}
        </button>
      }
    >
      {isEmpty ? (
        <>
          <p>{lt("deleteEmptyFolder", { names })}</p>
          <div className="exdl-conflict__choices">
            <button
              type="button"
              className="exdl-choice exdl-choice--danger"
              onClick={() => onSubmit("moveUp")}
            >
              <strong>{lt("delete")}</strong>
            </button>
          </div>
        </>
      ) : (
        <>
          <p>
            {lt("deleteFolderContents", {
              names,
              drawings: drawingCount,
              folders: folderCount,
            })}
          </p>
          <div className="exdl-conflict__choices">
            <button
              type="button"
              className="exdl-choice exdl-choice--primary"
              onClick={() => onSubmit("moveUp")}
            >
              <strong>{lt("deleteFolderKeep", { parent: parentName })}</strong>
              <span>{lt("deleteFolderKeepHint")}</span>
            </button>
            <button
              type="button"
              className="exdl-choice exdl-choice--danger"
              onClick={() => onSubmit("trash")}
            >
              <strong>{lt("deleteFolderAll")}</strong>
              <span>{lt("deleteFolderAllHint")}</span>
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};
